import { DATE_FORMATS } from '@callmart/types';
import { ColDef, ValueSetterParams } from 'ag-grid-community';
import { CellClassParams } from 'ag-grid-community/dist/types/core/entities/colDef';
import { getSlotTimeColumnDef } from '../../../utils/agGridUtils';
import { integerComparator } from '../../AgGrid/comparators';
import { getAllHourSlotsInATimeRange, getShortDayNameFromDate, getSlotDateTime } from '../../../utils/dateUtils';
import CellBorderRenderer from '../../AgGrid/CellBorderRenderer';
import { formatNumber, isNumberDecimal, parseLocalizedNumber } from '../../../utils/numberUtils';
import type { WorkingHour } from '../../../utils/workingHoursUtils';
import i18n from '../../../i18n';
import type { Dayjs } from '@utils/dayjs';

export type { WorkingHour };

export const STATIC_EXCEL_COLUMN_NAMES = ['Standort Detail', 'Stadt', 'Worktype', 'Kanal', 'Task', 'Zeit'];

export const defaultColumnDefs = {
    editable: false,
    filter: true,
    sortable: true,
    minWidth: 90,
};

interface VersionEntry {
    userId: number;
    role: 'TEF' | 'OSP';
    value: number;
    previousValue?: number | null;
    date: string;
    draft?: boolean;
}

export interface OfferedRecord {
    offeredReport?: {
        slotId?: string;
    };
    percentage?: number;
    total?: number;
    versions?: VersionEntry[] | null;
    lastEditedByRole?: 'TEF' | 'OSP' | null;
}

export interface PublicHoliday {
    date: string;
}

/**
 * Build an index Map for O(1) lookups by slot.
 * This replaces the O(n) .find() calls with O(1) Map.get() lookups.
 */
const buildSlotIndex = (records?: OfferedRecord[]): Map<string, OfferedRecord> => {
    const index = new Map<string, OfferedRecord>();
    records?.forEach((record) => {
        const slot = getSlotDateTime(record?.offeredReport?.slotId);
        if (slot) {
            index.set(slot, record);
        }
    });
    return index;
};

export const createColumnDefs = ({
    allDatesInAMonth,
    valuePropertyName,
    hasFooter,
    footerRowText: customFooterRowText,
    showPercentageSign,
    alwaysAllowEdit,
    editable = true,
    cellStyleRenderer,
    singleClickEdit,
    cellRenderer,
    cellRendererParams,
}: {
    allDatesInAMonth: Dayjs[];
    valuePropertyName: string;
    hasFooter?: boolean;
    footerRowText?: string;
    showPercentageSign?: boolean;
    alwaysAllowEdit?: boolean;
    singleClickEdit?: boolean;
    cellStyleRenderer?: (
        params: CellClassParams,
        additionalData: {
            dayKey: string;
            dayName: string;
            currentDate: Dayjs;
        }
    ) => any;
    editable?: boolean;
    cellRenderer?: any;
    cellRendererParams?: any;
}): ColDef[] => {
    const footerRowText = hasFooter ? (customFooterRowText ?? 'Sum') : '';
    const maxWidth = hasFooter ? 80 : 50;
    const columns: ColDef[] = [getSlotTimeColumnDef({ footerRowText, maxWidth }) as ColDef];

    for (const currentDate of allDatesInAMonth) {
        const dayOfWeek = currentDate.day();
        const shouldHighlightDay = dayOfWeek === 0;

        const dayName = getShortDayNameFromDate(currentDate, i18n.language);
        const dayKey = `day${currentDate.format(DATE_FORMATS.DAY_ONLY)}`;

        columns.push({
            field: `day${currentDate.format(DATE_FORMATS.DAY_ONLY)}`,
            headerName: `${dayName}\n${currentDate.format(DATE_FORMATS.DAY_MONTH)}`,
            resizable: true,
            singleClickEdit,
            comparator: (valueA: number, valueB: number) => {
                return integerComparator({ valueA, valueB });
            },
            editable: (params) => {
                if (!editable) return false;
                const dayData = params?.data?.[dayKey];

                if (!dayData?.isWorking) return false;

                if (valuePropertyName === 'total') {
                    return alwaysAllowEdit || !dayData?.released;
                }

                return false;
            },
            valueGetter: (params) => {
                const dayData = params?.data?.[dayKey];
                const value = dayData?.[valuePropertyName];

                if (value === undefined || value === null) return;

                // Keep this a number: the comparator, the filter and the caller's cellStyleRenderer
                // read params.value and parseInt it — "1.234,5 %" reads back as 1. Suffix goes in
                // the valueFormatter below.
                return value;
            },
            valueSetter: (params: ValueSetterParams) => {
                // Handle empty string as null (deletion), invalid input as null
                const newValue = parseLocalizedNumber(params.newValue);

                if (!params.data) {
                    return false;
                }

                // Reject negative numbers and decimals (only accept integers or null)
                if (newValue !== null && (newValue < 0 || isNumberDecimal(newValue))) {
                    return false;
                }

                if (!params.data[dayKey]) {
                    params.data[dayKey] = {};
                }

                const dayData = params?.data[dayKey];

                // Allow null (deletion) and valid numbers
                if (newValue !== null || params.newValue === '' || params.newValue === null) {
                    dayData[valuePropertyName] = newValue;
                    dayData[`${valuePropertyName}Changed`] = true;
                    return true;
                }

                // Reject invalid input (null from parseLocalizedNumber)
                return false;
            },
            cellRenderer: cellRenderer || CellBorderRenderer,
            cellRendererParams: cellRendererParams || { shouldHighlightDay },
            aggFunc: 'sum',
            valueFormatter: (params) => {
                if (params.value == null) return '';
                // No footer rounding: formatNumber's 2-decimal cap already absorbs the float noise
                // of an aggregated sum (722.2599999999999 -> "722,26").
                const formatted = formatNumber(params.value);
                return showPercentageSign ? `${formatted} %` : formatted;
            },
            cellStyle: (params: CellClassParams) => {
                const dayData = params?.data?.[dayKey];

                const styles = {
                    ...(dayData?.totalChanged && { backgroundColor: 'rgba(0, 0, 0, 0.5)' }), // couldn't find the totalChanged
                    ...(cellStyleRenderer && cellStyleRenderer(params, { dayKey, dayName, currentDate })),
                };

                return Object.keys(styles).length > 0 ? styles : null;
            },
        });
    }

    return columns;
};

const addRowsInMap = ({
    dates,
    biddingCalculated,
    correctedTotals,
    comparisonTotals,
    biddingCalculatedResults,
    correctedResults,
    rowsMap,
    workingHours,
    publicHolidays,
}: {
    dates: any[];
    rowsMap: Map<string, any>;
    biddingCalculated?: OfferedRecord[];
    correctedTotals?: OfferedRecord[];
    comparisonTotals?: OfferedRecord[];
    biddingCalculatedResults?: OfferedRecord[];
    correctedResults?: OfferedRecord[];
    workingHours?: WorkingHour[];
    publicHolidays?: PublicHoliday[];
}) => {
    const allHourSlots = getAllHourSlotsInATimeRange({
        startTime: 0,
        endTime: 23,
        displayHoursInHH: true,
        displayMinutesInmm: true,
    });
    // Build O(1) lookup indexes ONCE before the loops (was O(n) per lookup before)
    const biddingIndex = buildSlotIndex(biddingCalculated);
    const correctedTotalsIndex = buildSlotIndex(correctedTotals);
    const comparisonTotalsIndex = buildSlotIndex(comparisonTotals);
    const resultsIndex = buildSlotIndex(biddingCalculatedResults);
    const correctedResultsIndex = buildSlotIndex(correctedResults);

    // Build working hours index for O(1) lookup
    const workingHoursIndex = new Map<string, WorkingHour>();
    workingHours?.forEach((wh) => {
        const key = `${wh.dayTimeSlot.day}-${wh.dayTimeSlot.timeSlot}`;
        workingHoursIndex.set(key, wh);
    });

    // Build public holidays set for O(1) lookup
    const publicHolidaysSet = new Set(publicHolidays?.map((h) => h.date) || []);

    for (const hourSlot of allHourSlots) {
        const valuesPerDay: {
            [k: string]: {
                date: string;
                total?: number;
                percentage?: number;
                totalChanged?: boolean;
                percentageChanged?: boolean;
                released?: boolean;
                releaseDate?: string;
                createdAt?: string;
                updatedAt?: string;
                difference?: number;
                isWorking?: boolean;
            };
        } = {};
        for (const date of dates) {
            const isPublicHoliday = publicHolidaysSet.has(date.format(DATE_FORMATS.ISO_DATE));
            const dayNumberForDate = isPublicHoliday ? 8 : date.day() === 0 ? 7 : date.day();
            const workingHourKey = `${dayNumberForDate}-${hourSlot}`;
            const workingHour = workingHoursIndex.get(workingHourKey);

            const slot = `${date.format(DATE_FORMATS.DATE)}-${hourSlot}`;
            // O(1) lookups instead of O(n) .find() calls
            const biddingCalculatedRecord = biddingIndex.get(slot);
            const correctedTotal = correctedTotalsIndex.get(slot);
            const comparisonTotal = comparisonTotalsIndex.get(slot);
            const biddingCalculatedResult = resultsIndex.get(slot);
            const correctedResult = correctedResultsIndex.get(slot);

            // Include comparisonValue if it exists and differs from current total
            const currentValue = correctedTotal?.total;
            const comparisonValue = comparisonTotal?.total;
            const hasReleasedValue = currentValue != null;
            const hasDraftValue = comparisonValue != null;
            const hasComparisonDiff = hasDraftValue && hasReleasedValue && comparisonValue !== currentValue;
            // Draft-only: when viewing released but only draft exists (TEF sees OSP drafts)
            const isDraftOnly = !hasReleasedValue && hasDraftValue;

            valuesPerDay[`day${date.format(DATE_FORMATS.DAY_ONLY)}`] = {
                date: slot,
                ...(biddingCalculatedRecord && {
                    ...biddingCalculatedRecord,
                    percentage: Number.isFinite(biddingCalculatedRecord?.percentage)
                        ? Math.round((biddingCalculatedRecord?.percentage || 0) * 100)
                        : biddingCalculatedRecord?.percentage,
                }),
                ...(correctedTotal && {
                    ...correctedTotal,
                }),
                ...(biddingCalculatedResult && {
                    ...biddingCalculatedResult,
                }),
                ...(correctedResult && {
                    ...correctedResult,
                }),
                // Draft-only: show draft value when no released value exists
                ...(isDraftOnly && {
                    total: comparisonTotal!.total,
                    isDraftOnly: true,
                    draftValue: comparisonTotal!.total,
                    versions: comparisonTotal!.versions,
                    // Don't copy lastEditedByRole - we don't show the role triangle for draft-only
                }),
                // Add comparison value for diff highlighting (only if there's a difference)
                ...(hasComparisonDiff && {
                    comparisonValue,
                }),
                // Include comparison versions for merged history (when draft exists alongside released)
                // Skip if isDraftOnly - versions already contains the draft versions
                ...(!isDraftOnly &&
                    hasDraftValue &&
                    comparisonTotal?.versions && {
                        comparisonVersions: comparisonTotal.versions,
                    }),
                difference: (correctedTotal?.total || 0) - (biddingCalculatedRecord?.total || 0),
                isWorking: workingHour?.isWorking,
            };
        }

        rowsMap.set(hourSlot, valuesPerDay);
    }

    return rowsMap;
};

export const createMapWithTableRows = ({
    dates,
    biddingCalculated,
    correctedTotals,
    comparisonTotals,
    biddingCalculatedResults,
    correctedResults,
    workingHours,
    publicHolidays,
}: {
    dates: any[];
    biddingCalculated?: OfferedRecord[];
    correctedTotals?: OfferedRecord[];
    comparisonTotals?: OfferedRecord[];
    biddingCalculatedResults?: OfferedRecord[];
    correctedResults?: OfferedRecord[];
    workingHours?: any[];
    publicHolidays?: any[];
}) => {
    let rowsMap = new Map();
    rowsMap = addRowsInMap({
        dates,
        biddingCalculated,
        correctedTotals,
        comparisonTotals,
        biddingCalculatedResults,
        correctedResults,
        rowsMap,
        workingHours,
        publicHolidays,
    });

    return rowsMap;
};

export const filterChangedRows = (rows: { date: string; [k: string]: any }[]) => {
    const result: {
        total: number;
        released: boolean;
        releaseDate: null;
        createdAt: string;
        updatedAt: string;
        date: string;
        [k: string]: any;
    }[] = [];
    rows.forEach((row) => {
        const obj = row;
        Object.keys(obj).forEach((dayKey) => {
            if (!dayKey.includes('day')) return;

            const dayData = obj[dayKey];
            const keysWithChanged = Object.keys(dayData).filter((key) => key.includes('Changed'));
            if (keysWithChanged.length === 0) return;

            const key = keysWithChanged[0].split('Changed')[0];
            result.push({
                ...dayData,
                total: dayData[key],
                released: false,
                releaseDate: null,
                createdAt: new Date(),
                updatedAt: new Date(),
            });
        });
    });

    return result;
};
